import React, { useState, useEffect } from 'react'
import ReactGA from 'react-ga4';
import axios from "axios";
import { Auth } from "aws-amplify";
import { AppBar, Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';

import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import CompareReport from './CompareReport';
import BarChart from '../../BarChart';
import LineChart from '../../LineChart';
import Table from '../../TableComponent';
import { CSVLink } from 'react-csv';

const Report = ({ adminInfo }) => {
  const [reportData, setReportData] = useState("")
  const [reportError, setReportError] = useState("")
  const [testId, setTestId] = useState(adminInfo.allTests ? adminInfo.allTests[0].testId : null)
  const [tabValue, setTabValue] = useState(0);
  const [std, setStd] = useState("");  
  const [lang, setLang] = useState(adminInfo.allTests ? adminInfo.allTests[0].lang.sort()[0] : "");
  const [school, setSchool] = useState("");
  const [metric, setMetric] = useState("");
  const [chartType, setChartType] = useState("");
  const [showPhrasingNotQualified, setShowPhrasingNotQualified] = useState(true);

  const { t } = useTranslation();
  
  useEffect(() => {
    if(adminInfo.allTests) {
      Auth.currentSession()
      .then((data) => {
        const jwtToken = data.idToken.jwtToken;
        
        const getReportData = async(adminId, testId, lang) => {
          try {
            const response =  await axios.get("https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com/alpha/admin/"+adminId+"/result?testId="+testId+"&ver=2", {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': jwtToken
                }
            });
            // // console.log("Report Data: ",response.data["report"])

            // Remove keys from the metric object
            // let metricFilters = []
            let metricFilters = ["pace", "accuracy"]
            let filteredMetric = Object.fromEntries(
              Object.entries(response.data.report.metric).map(([metricLang, metrics]) => {
                let filtered = Object.fromEntries(
                  Object.entries(metrics).filter(([key]) => !metricFilters.includes(key))
                );
                return [metricLang, filtered];
              })
            );
            
            const studentResponse =  await axios.get("https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com/alpha/admin/"+adminId+"/report?testId="+testId, {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': jwtToken
              }
            });
            let reportData = {...response.data["report"],
              "metric": filteredMetric,
              "students": studentResponse.data["report"]["students"].sort((a,b) => (a.std - b.std || a.divn.localeCompare(b.divn) || a.rollNo - b.rollNo)),
              "previousStudentResult": studentResponse.data["report"]["previousStudentResult"]
            }

            setSchool(reportData.school[lang].map(item => item.schoolName).sort()[0] || "")
            setStd(reportData.school[lang].map(item => item.std).sort((a, b) => a - b)[0] || "")
            let metric = Object.keys(reportData.metric[lang])[0]
            setMetric(metric)
            setChartType(reportData.metric[lang][metric][0])
            if(!reportData.school[lang].length) setReportError(t("admin.results.error_api_resultNotFound"))
            setReportData(reportData) 
          }
          catch (error) {
            console.log(error)
            if(error.response.status === 404) setReportError(t("admin.results.error_api_resultNotFound"))
            else console.log(error)
          }
        }
  
        if(!reportData) getReportData(adminInfo.admin.adminId, testId, lang)
        
      })
      .catch((err) => console.log(err));
    }
    else{
      alert(t("commonItems.error_noTestCompleted"))
      window.location.replace("/");
    }
    
  }, [t, adminInfo, reportData, testId, lang]);

  const columns = [
    {
      key: "studentName",
      headerName: t("commonItems.tableHeader_studentName"),
      width: 60,
      color: "catColor"
    },
    {
      key: "orgStudId",
      headerName: t("commonItems.tableHeader_orgStudId"),
      width: 80
    },
    { 
      key: "classSection", 
      headerName: t("commonItems.tableHeader_classSection"), 
      width: 30 
    },
    { 
      key: "rollNo", 
      headerName: t("commonItems.tableHeader_rollNo"), 
      width: 30 
    },
    { 
      key: "gender", 
      headerName: t("commonItems.tableHeader_gender"), 
      width: 30 
    },
    { 
      key: "readerCatNum", 
      headerName: t("commonItems.tableHeader_readerCategory"), 
      width: 80,
      readerCat: "readerCat",
      previousCategory: "previousCategory",
      tooltip: true
    }
  ];

  const headers = [
    { label: "Class", key: "std" },
    { label: "Section", key: "divn" },
    { label: "Student Name", key: "studentName" },
    { label: "Gender", key: "gender" },
    { label: "Org Student Id", key: "orgStudId"},
    // { label: "Student PEN (Maximum 12 digit)", key: "penNo"},
    { label: "Roll No", key: "rollNo" },
    { label: "Reading Category", key: "readerCat"},
    { label: "WCPM (" + adminInfo.allLangs[lang] +")", key: "wcpm" },
    { label: "WCPM Category (" + adminInfo.allLangs[lang] +")", key: "wcpmCat" },
    { label: "Phrasing (" + adminInfo.allLangs[lang] +")", key: "phrasingCat" },
  ];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // console.log(event.target.firstChild.data)
    ReactGA.event({
      category: 'Admin Results Tab',
      action: 'Result Tab Switch',
      label: event.target.firstChild.data
    });
  };

  const handleTestIdChange = (e) => {
    setStd("");
    setSchool("");
    setMetric("");
    setChartType("");
    setReportData("");
    setReportError("");
    let selectedTestLangs = adminInfo.allTests.filter(item => item.testId === e.target.value)[0].lang
    if(!selectedTestLangs.includes(lang)) setLang(selectedTestLangs.sort()[0])
    setTestId(e.target.value);
  };

  const handleStdChange = (e) => {
    setStd(e.target.value);
  };
  
  const handleLangChange = (e) => {
    let lang = e.target.value
    if(reportData!==""){
      let schools = [...new Set(reportData.school[lang].map(item => item.schoolName))].sort()
      if(!schools.includes(std)) setSchool(schools[0])
      let stds = [...new Set(reportData.school[lang].map(item => item.std))].sort((a, b) => a - b)
      if(!stds.includes(std)) setStd(stds[0])
      let metrics = Object.keys(reportData["metric"][lang])
      if(!metrics.includes(metric)){
        setMetric(metrics[0])
        setChartType(reportData["metric"][lang][metrics[0]][0])
      }
    }
    setLang(lang);
  };
  
  const handleSchoolChange = (e) => {
    setSchool(e.target.value);
  };

  const handleMetricChange = (e) => {
    if(reportData!==""){
      let chartTypes = reportData["metric"][lang][e.target.value]
      if(!chartTypes.includes(chartType)) setChartType(chartTypes[0])
    }
    setMetric(e.target.value);
  };

  const handleChartTypeChange = (e) => {
    setChartType(e.target.value);
  };

  const handleShowPhrasingNotQualified = (e) => {
    setShowPhrasingNotQualified(e.target.checked);
  };

  var filteredReportData = reportData
  if(lang && std!== "") {
    filteredReportData = {
      "allSchools": reportData.allSchools[lang].filter(item => item.std === std)[0],
      "region": reportData.region[lang].filter(item => item.std === std)[0],
      "school": reportData.school[lang].filter(item => item.std === std),
      "students": reportData.students.filter(item => item.std === std),
      "previousStudentResult": reportData.previousStudentResult,
      "thresholds": reportData.thresholds[lang].filter(item => item.std === std)[0]
    }
  }
  // console.log(filteredReportData)
    
  return (
    <div className='report innerContent'>
      <h2 style={{textAlign: "center", color: "#0288D1", borderRadius: "15px", border: "1px solid"}}>
        {t("admin.results.title")}
      </h2>
      <div className="chartArea">
        <div >
          <AppBar position="static" sx={{mt: "0.5rem", backgroundColor: "#001162"}}>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{minHeight: "30px"}} variant='fullWidth' textColor="inherit" TabIndicatorProps={{style: {backgroundColor: "#7ed46a", height: "4px"}}}>
              <Tab label={t("commonItems.tabLabel_allSchools")} sx={{fontSize: "inherit", minHeight: "30px"}}/>
              <Tab label={t("commonItems.tabLabel_yourSchool")} sx={{fontSize: "inherit", minHeight: "30px"}}/>
              <Tab label={t("commonItems.tabLabel_studentWise")} sx={{fontSize: "inherit", minHeight: "30px"}}/>
              <Tab label={t("commonItems.tabLabel_compare")} sx={{fontSize: "inherit", minHeight: "30px"}}/>
              <Tab label={t("commonItems.tabLabel_download")} sx={{fontSize: "inherit", minHeight: "30px"}}/>
            </Tabs>
          </AppBar>
          { tabValue !== 3 ?
              <div style={{display: "flex", flexDirection: "column", paddingTop: "0.5rem"}}>
                {/* <h4 style={{color: "#dc3545"}}>Note: Invalid attempts are not considered in the results.</h4> */}
                <p style={{fontSize: "14px", color: "#dc3545"}}>
                  {adminInfo.allTests && adminInfo.allTests.filter(item=> item.testId === testId)[0].status === 4 ?
                    tabValue === 2 ?
                      t("commonItems.info_resultGeneratedStudentwise")
                    :
                      t("commonItems.info_resultGenerated", { 
                        date: new Date(adminInfo.allTests.filter(item=> item.testId === testId)[0].resultGeneratedDate).toLocaleDateString("en-GB", { dateStyle: "long" }) 
                      })
                  :
                    adminInfo.allTests &&     
                      t("commonItems.info_resultGenerated", { 
                        date: new Date(adminInfo.allTests.filter(item=> item.testId === testId)[0].uploadEndDate).toLocaleDateString("en-GB", { dateStyle: "long" }) 
                      })
                  }
                </p>
              </div>
            :
              <div style={{display: "flex", flexDirection: "column", paddingTop: "0.5rem"}}>
                <h4 style={{color: "#dc3545"}}>
                  {t("commonItems.info_compareTab")}
                </h4>
              </div>
          }
        </div>
        { tabValue === 3 ?
          <CompareReport adminInfo={adminInfo}/>
        :
          <>
            <div className="filters">
              <div className="forms">
                <h4 style={{display: "flex", justifyContent: "center", alignItems: "center", gap: "0.25rem"}}>
                  <FilterListIcon fontSize="small"/>
                  <span style={{height: "100%"}}>{t("commonItems.filterLabel")}</span> 
                </h4>
                <FormControl sx={{ m: 1, minWidth: 150}} size="small">
                  <InputLabel id="testNameFilterLabel">{t("commonItems.dropdownLabel_testName")}</InputLabel>
                  <Select
                    labelId="testIdFilterLabel"
                    id="testIdFilter"
                    value={testId ? testId : ""}
                    onChange={handleTestIdChange}
                    autoWidth
                    label={t("commonItems.dropdownLabel_testName")}
                  >
                    {/* <MenuItem value="">
                      <em>None</em>
                    </MenuItem> */}
                    {/* getting all unique std values from data */}
                    {adminInfo.allTests &&
                      adminInfo.allTests.map(item => item.testName).map((item, i) => {
                      return <MenuItem key={i} value={adminInfo.allTests[i].testId}>{item}</MenuItem>
                      })
                    }
                  </Select>
                </FormControl>
                {tabValue !==4 &&
                  <>
                    <FormControl sx={{ m: 1, minWidth: 150}} size="small">
                      <InputLabel id="langFilterLabel">{t("commonItems.dropdownLabel_lang")}</InputLabel>
                      <Select
                        labelId="langFilterLabel"
                        id="langFilter"
                        value={lang}
                        onChange={handleLangChange}
                        autoWidth
                        label={t("commonItems.dropdownLabel_lang")}
                      >
                        {adminInfo.allTests &&
                          adminInfo.allTests.filter(item => item.testId === testId)[0].lang.sort().map((item, i) => {
                            return <MenuItem key={i} value={item}>{adminInfo.allLangs[item]}</MenuItem>   
                          })
                        }
                      </Select>
                    </FormControl>
                    <FormControl sx={{ m: 1, minWidth: 85}} size="small">
                      <InputLabel id="stdFilterLabel">{t("commonItems.dropdownLabel_class")}</InputLabel>
                      <Select
                        labelId="stdFilterLabel"
                        id="stdFilter"
                        value={std}
                        onChange={handleStdChange}
                        autoWidth
                        label={t("commonItems.dropdownLabel_class")}
                      >
                        {/* <MenuItem value="">
                          <em>None</em>
                        </MenuItem> */}
                        {/* getting all unique std values from data */}
                        { reportData!=="" && lang &&
                          [...new Set(reportData.school[lang].map(item => item.std))].sort((a, b) => a - b).map((item, i) => {
                          return <MenuItem key={i} value={item}>{item}</MenuItem>
                          })
                        }
                      </Select>
                    </FormControl>
                    {tabValue !== 2 &&
                      <FormControl sx={{ m: 1, minWidth: 120}} size="small">
                        <InputLabel id="metricFilterLabel">{t("commonItems.dropdownLabel_metric")}</InputLabel>
                        <Select
                          labelId="metricFilterLabel"
                          id="metricFilter"
                          value={metric}
                          onChange={handleMetricChange}
                          autoWidth
                          label={t("commonItems.dropdownLabel_metric")}
                        >
                          { reportData!=="" && lang &&
                            Object.keys(reportData["metric"][lang]).map((item, i) => {
                              return <MenuItem key={i} value={item}>{
                                item==="wcpm" ? "WCPM" : 
                                item==="phrasing" ? "Phrasing" : 
                                item==="pace" ? "Pace" :
                                item==="accuracy" ? "Accuracy" :
                                item 
                              }</MenuItem>
                            })
                          }
                        </Select>
                      </FormControl>
                    }
                    {tabValue !== 2 &&
                      <FormControl sx={{ m: 1, minWidth: 130}} size="small">
                        <InputLabel id="chartTypeFilterLabel">{t("commonItems.dropdownLabel_chartType")}</InputLabel>
                        <Select
                          labelId="chartTypeFilterLabel"
                          id="chartTypeFilter"
                          value={chartType}
                          onChange={handleChartTypeChange}
                          autoWidth
                          label={t("commonItems.dropdownLabel_chartType")}
                        >
                          { reportData!=="" && lang && metric &&
                            reportData["metric"][lang][metric].map((item, i) => {
                              return <MenuItem key={i} value={item}>{item==="bar" ? "Bar" : item==="line" ? "Line" : item }</MenuItem>
                            })
                          }
                        </Select>
                      </FormControl>
                    }
                    {tabValue === 1 && 
                      <FormControl sx={{ m: 1, minWidth: 150}} size="small">
                        <InputLabel id="schoolFilterLabel">{t("commonItems.dropdownLabel_school")}</InputLabel>
                        <Select
                          labelId="schoolFilterLabel"
                          id="schoolFilter"
                          value={school}
                          onChange={handleSchoolChange}
                          autoWidth
                          label={t("commonItems.dropdownLabel_school")}
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          { reportData!=="" && lang &&
                            [...new Set(reportData.school[lang].map(item => item.schoolName))].sort().map((item, i) => {
                            return <MenuItem key={i} value={item}>{item}</MenuItem>
                            })
                          }
                        </Select>
                      </FormControl>
                    }
                    {tabValue !== 2 && metric === "phrasing" && chartType === "bar" &&
                      <FormControlLabel 
                        control={
                          <Checkbox 
                            checked={showPhrasingNotQualified}
                            onChange={handleShowPhrasingNotQualified}
                          />
                        } 
                        label={t("commonItems.checkboxLabel_showNotQualfied")} 
                        sx={{ml: -0.5, mr: 1}}
                      />
                    }
                  </>
                }
              </div>
            </div>
              {!reportError ?
                <>
                  {(reportData!=="" && lang && tabValue === 0 ) &&
                    <div className="charts">
                      {metric === "wcpm" &&
                        <>
                          {chartType === "bar" && 
                            <BarChart
                              chartData = {
                                [
                                  [filteredReportData.allSchools.wcpmCat1, filteredReportData.allSchools.wcpmCat2, filteredReportData.allSchools.wcpmCat3, filteredReportData.allSchools.wcpmCat4, filteredReportData.allSchools.wcpmCat5, filteredReportData.allSchools.wcpmTotal], 
                                  [filteredReportData.region.wcpmCat1, filteredReportData.region.wcpmCat2, filteredReportData.region.wcpmCat3, filteredReportData.region.wcpmCat4, filteredReportData.region.wcpmCat5, filteredReportData.region.wcpmTotal], 
                                  filteredReportData.school.reduce((acc, item) => {
                                    return [
                                      acc[0] + item.wcpmCat1,
                                      acc[1] + item.wcpmCat2,
                                      acc[2] + item.wcpmCat3,
                                      acc[3] + item.wcpmCat4,
                                      acc[4] + item.wcpmCat5,
                                      acc[5] + item.wcpmTotal,
                                    ]
                                  }, [0, 0, 0, 0, 0, 0])
                                ]
                              }
                              catLabelData = {
                                [
                                  filteredReportData.thresholds.wcpmT1 < 0 ? "Invalid Entries" : t("commonItems.chartCatLabel_bar_wcpm1", {t1: 0, t2: filteredReportData.thresholds.wcpmT1}),
                                  t("commonItems.chartCatLabel_bar_wcpm1", {t1: filteredReportData.thresholds.wcpmT1+1, t2: filteredReportData.thresholds.wcpmT2}),
                                  t("commonItems.chartCatLabel_bar_wcpm1", {t1: filteredReportData.thresholds.wcpmT2+1, t2: filteredReportData.thresholds.wcpmT3}),
                                  filteredReportData.thresholds.wcpmT4 < 0 ?  t("commonItems.chartCatLabel_bar_wcpm2", {t1: filteredReportData.thresholds.wcpmT3+1}) : t("commonItems.chartCatLabel_bar_wcpm1", {t1: filteredReportData.thresholds.wcpmT3+1, t2: filteredReportData.thresholds.wcpmT4}),
                                  filteredReportData.thresholds.wcpmT4 < 0 ? "-" : t("commonItems.chartCatLabel_bar_wcpm2", {t1: filteredReportData.thresholds.wcpmT4+1})
                                ]
                              }
                              labelData = {[t("admin.results.allSchoolsTab.chartLabel1"), t("admin.results.allSchoolsTab.chartLabel2"), t("admin.results.allSchoolsTab.chartLabel3")]}
                              title = {"WCPM - " + t("admin.results.allSchoolsTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                              xLabel = {t("admin.results.allSchoolsTab.chartXLabel", {std: std})}
                              yLabel = {t("admin.results.allSchoolsTab.chartYLabel", {unit: "%"})}
                              chartName={"Class-" + std}
                              chartInfo={
                                (filteredReportData.school[0].recCount && filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0)!==0) && 
                                  t("commonItems.info_chart", { 
                                    tested: filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0).toLocaleString("hi"),
                                    total: filteredReportData.school.reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                  })  
                              }
                            /> 
                          }
                          {chartType === "line" &&
                            <LineChart 
                              chartData = {[
                                            [
                                              filteredReportData.allSchools.wcpmMedian,
                                              filteredReportData.region.wcpmMedian,
                                              Math.round(filteredReportData.school.reduce((acc, item) => {
                                                return [
                                                  acc[0] + (item.wcpmMedian)/filteredReportData.school.length,
                                                ]
                                              }, [0])[0])
                                            ]
                                          ]}
                              catLabelData={
                                [t("commonItems.chartCatLabel_line_wcpm1")]
                              }
                              labelData = {[t("admin.results.allSchoolsTab.chartLabel1"), t("admin.results.allSchoolsTab.chartLabel2"), t("admin.results.allSchoolsTab.chartLabel3")]}
                              title = {"WCPM - " + t("admin.results.allSchoolsTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                              xLabel = {t("admin.results.allSchoolsTab.chartXLabel", {std: std})}
                              yLabel = {t("admin.results.allSchoolsTab.chartYLabel", {unit: "WCPM"})}
                              chartName={"Class-" + std}
                              chartInfo={
                                (filteredReportData.school[0].recCount && filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0)!==0) && 
                                  t("commonItems.info_chart", { 
                                    tested: filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0).toLocaleString("hi"),
                                    total: filteredReportData.school.reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                  })  
                              }
                            />
                          }
                        </>
                      }
                      {metric === "phrasing" &&
                        <> 
                          {Object.keys(filteredReportData.thresholds).includes("phrasingT1") ?
                            <>
                              {chartType === "bar" &&
                                <BarChart
                                  chartData = {
                                    showPhrasingNotQualified ?
                                      [
                                        [(filteredReportData.allSchools.wcpmTotal - filteredReportData.allSchools.phrasingTotal), filteredReportData.allSchools.phrasingCat1, filteredReportData.allSchools.phrasingCat2, filteredReportData.allSchools.phrasingCat3, filteredReportData.allSchools.phrasingCat4, filteredReportData.allSchools.wcpmTotal], 
                                        [(filteredReportData.region.wcpmTotal - filteredReportData.region.phrasingTotal), filteredReportData.region.phrasingCat1, filteredReportData.region.phrasingCat2, filteredReportData.region.phrasingCat3, filteredReportData.region.phrasingCat4, filteredReportData.region.wcpmTotal], 
                                        filteredReportData.school.reduce((acc, item) => {
                                          return [acc[0] + (item.wcpmTotal - item.phrasingTotal), acc[1] + item.phrasingCat1, acc[2] + item.phrasingCat2, acc[3] + item.phrasingCat3, acc[4] + item.phrasingCat4, acc[5] + item.wcpmTotal]
                                        }, [0, 0, 0, 0, 0, 0])
                                      ]
                                    :
                                      [
                                        [0, filteredReportData.allSchools.phrasingCat1, filteredReportData.allSchools.phrasingCat2, filteredReportData.allSchools.phrasingCat3, filteredReportData.allSchools.phrasingCat4, filteredReportData.allSchools.phrasingTotal], 
                                        [0, filteredReportData.region.phrasingCat1, filteredReportData.region.phrasingCat2, filteredReportData.region.phrasingCat3, filteredReportData.region.phrasingCat4, filteredReportData.region.phrasingTotal], 
                                        filteredReportData.school.reduce((acc, item) => {
                                          return [acc[0] + 0, acc[1] + item.phrasingCat1, acc[2] + item.phrasingCat2, acc[3] + item.phrasingCat3, acc[4] + item.phrasingCat4, acc[5] + item.phrasingTotal]
                                        }, [0, 0, 0, 0, 0, 0])
                                      ]
                                  }
                                  catLabelData={ 
                                    showPhrasingNotQualified ?
                                      [
                                        // "Phrasing > " + filteredReportData.thresholds.phrasingT1,
                                        // "Phrasing between " + (filteredReportData.thresholds.phrasingT2) + " - " + filteredReportData.thresholds.phrasingT1,
                                        // "Phrasing between 0 - " + filteredReportData.thresholds.phrasingT2
                                        t("commonItems.chartCatLabel_bar_phrasing1"), 
                                        t("commonItems.chartCatLabel_bar_phrasing2"), 
                                        t("commonItems.chartCatLabel_bar_phrasing3"), 
                                        t("commonItems.chartCatLabel_bar_phrasing4"), 
                                        t("commonItems.chartCatLabel_bar_phrasing5")
                                      ]
                                    :
                                      [
                                        "-", 
                                        t("commonItems.chartCatLabel_bar_phrasing2"), 
                                        t("commonItems.chartCatLabel_bar_phrasing3"), 
                                        t("commonItems.chartCatLabel_bar_phrasing4"), 
                                        t("commonItems.chartCatLabel_bar_phrasing5")
                                      ]
                                  }
                                  labelData = {[t("admin.results.allSchoolsTab.chartLabel1"), t("admin.results.allSchoolsTab.chartLabel2"), t("admin.results.allSchoolsTab.chartLabel3")]}
                                  title = {"Phrasing - " + t("admin.results.allSchoolsTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.allSchoolsTab.chartXLabel", {std: std})}
                                  yLabel = {t("admin.results.allSchoolsTab.chartYLabel", {unit: "%"})}
                                  chartName={"Class-" + std}
                                  chartInfo={
                                    (filteredReportData.school[0].recCount && filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0).toLocaleString("hi"),
                                        total: filteredReportData.school.reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                />
                              }
                              {chartType === "line" &&
                                <LineChart 
                                  chartData = {[
                                                [
                                                  filteredReportData.allSchools.phrasingMedian,
                                                  filteredReportData.region.phrasingMedian,
                                                  Math.round(filteredReportData.school.reduce((acc, item) => {
                                                    return [
                                                      acc[0] + (item.phrasingMedian)/filteredReportData.school.length,
                                                    ]
                                                  }, [0])[0])
                                                ]
                                              ]}
                                  catLabelData={
                                    [t("commonItems.chartCatLabel_line_phrasing1")]
                                  }
                                  labelData = {[t("admin.results.allSchoolsTab.chartLabel1"), t("admin.results.allSchoolsTab.chartLabel2"), t("admin.results.allSchoolsTab.chartLabel3")]}
                                  title = {"Phrasing - " + t("admin.results.allSchoolsTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.allSchoolsTab.chartXLabel", {std: std})}
                                  yLabel = {t("admin.results.allSchoolsTab.chartYLabel", {unit: "Phrasing"})}
                                  chartName={"Class-" + std}
                                  chartInfo={
                                    (filteredReportData.school[0].recCount && filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0).toLocaleString("hi"),
                                        total: filteredReportData.school.reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                />
                              }
                            </>
                          :
                            <h3 style={{placeSelf: "center"}}>{t("commonItems.error_metricNotFound", {metric: "Phrasing"})}</h3>
                          }
                        </>
                      }
                      {metric === "pace" &&
                        <> 
                          {Object.keys(filteredReportData.thresholds).includes("paceT1") ?
                            <>
                              {chartType === "bar" && 
                                <BarChart
                                  chartData = {
                                    [
                                      [filteredReportData.allSchools.paceCat1, filteredReportData.allSchools.paceCat2, filteredReportData.allSchools.paceCat3, filteredReportData.allSchools.paceCat4, filteredReportData.allSchools.paceTotal], 
                                      [filteredReportData.region.paceCat1, filteredReportData.region.paceCat2, filteredReportData.region.paceCat3, filteredReportData.region.paceCat4, filteredReportData.region.paceTotal], 
                                      filteredReportData.school.reduce((acc, item) => {
                                        return [
                                          acc[0] + item.paceCat1,
                                          acc[1] + item.paceCat2,
                                          acc[2] + item.paceCat3,
                                          acc[3] + item.paceCat4,
                                          acc[4] + item.paceTotal
                                        ]
                                      }, [0, 0, 0, 0, 0])
                                    ]
                                  }
                                  catLabelData = {
                                    [
                                      // "Slow", "Medium", "Fast", "Very Fast"
                                      t("commonItems.chartCatLabel_bar_pace1", {t1: (parseFloat(filteredReportData.thresholds.paceT1) + 0.1).toFixed(1)}),
                                      t("commonItems.chartCatLabel_bar_pace2", {t1: (parseFloat(filteredReportData.thresholds.paceT1) + 0.1).toFixed(1), t2: (parseFloat(filteredReportData.thresholds.paceT2) + 0.1).toFixed(1)}),
                                      t("commonItems.chartCatLabel_bar_pace3", {t1: (parseFloat(filteredReportData.thresholds.paceT2) + 0.1).toFixed(1), t2: (parseFloat(filteredReportData.thresholds.paceT3) + 0.1).toFixed(1)}),
                                      t("commonItems.chartCatLabel_bar_pace4", {t1: (parseFloat(filteredReportData.thresholds.paceT3) + 0.1).toFixed(1)})
                                    ]
                                  }
                                  labelData = {[t("admin.results.allSchoolsTab.chartLabel1"), t("admin.results.allSchoolsTab.chartLabel2"), t("admin.results.allSchoolsTab.chartLabel3")]}
                                  title = {"Pace - " + t("admin.results.allSchoolsTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.allSchoolsTab.chartXLabel", {std: std})}
                                  yLabel = {t("admin.results.allSchoolsTab.chartYLabel", {unit: "%"})}
                                  chartName={"Class-" + std}
                                  chartInfo={
                                    (filteredReportData.school[0].recCount && filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.paceTotal), 0)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.paceTotal), 0).toLocaleString("hi"),
                                        total: filteredReportData.school.reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                /> 
                              }
                              {chartType === "line" &&
                                <LineChart 
                                  chartData = {[
                                                [
                                                  filteredReportData.allSchools.paceMedian,
                                                  filteredReportData.region.paceMedian,
                                                  Math.round(filteredReportData.school.reduce((acc, item) => {
                                                    return [
                                                      acc[0] + (item.paceMedian)/filteredReportData.school.length,
                                                    ]
                                                  }, [0])[0])
                                                ]
                                              ]}
                                  catLabelData={
                                    [t("commonItems.chartCatLabel_line_pace1")]
                                  }
                                  labelData = {[t("admin.results.allSchoolsTab.chartLabel1"), t("admin.results.allSchoolsTab.chartLabel2"), t("admin.results.allSchoolsTab.chartLabel3")]}
                                  title = {"Pace - " + t("admin.results.allSchoolsTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.allSchoolsTab.chartXLabel", {std: std})}
                                  yLabel = {t("admin.results.allSchoolsTab.chartYLabel", {unit: "Pace"})}
                                  chartName={"Class-" + std}
                                  chartInfo={
                                    (filteredReportData.school[0].recCount && filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.paceTotal), 0)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.paceTotal), 0).toLocaleString("hi"),
                                        total: filteredReportData.school.reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                />
                              }
                            </>
                          :
                            <h3 style={{placeSelf: "center"}}>{t("commonItems.error_metricNotFound", {metric: "Pace"})}</h3>
                          }
                        </>
                      }
                      {metric === "accuracy" &&
                        <> 
                          {Object.keys(filteredReportData.thresholds).includes("accuracyT1") ?
                            <>
                              {chartType === "bar" && 
                                <BarChart
                                  chartData = {
                                    [
                                      [filteredReportData.allSchools.accuracyCat1, filteredReportData.allSchools.accuracyCat2, filteredReportData.allSchools.accuracyCat3, filteredReportData.allSchools.accuracyCat4, filteredReportData.allSchools.accuracyTotal], 
                                      [filteredReportData.region.accuracyCat1, filteredReportData.region.accuracyCat2, filteredReportData.region.accuracyCat3, filteredReportData.region.accuracyCat4, filteredReportData.region.accuracyTotal], 
                                      filteredReportData.school.reduce((acc, item) => {
                                        return [
                                          acc[0] + item.accuracyCat1,
                                          acc[1] + item.accuracyCat2,
                                          acc[2] + item.accuracyCat3,
                                          acc[3] + item.accuracyCat4,
                                          acc[4] + item.accuracyTotal
                                        ]
                                      }, [0, 0, 0, 0, 0])
                                    ]
                                  }
                                  catLabelData = {
                                    [
                                      // "Poor", "Fair", "Good", "Excellent"
                                      t("commonItems.chartCatLabel_bar_accuracy1", {t1: (filteredReportData.thresholds.accuracyT1+1)}),
                                      t("commonItems.chartCatLabel_bar_accuracy2", {t1: (filteredReportData.thresholds.accuracyT1+1), t2: (filteredReportData.thresholds.accuracyT2+1)}),
                                      t("commonItems.chartCatLabel_bar_accuracy3", {t1: (filteredReportData.thresholds.accuracyT2+1), t2: (filteredReportData.thresholds.accuracyT3+1)}),
                                      t("commonItems.chartCatLabel_bar_accuracy4", {t1: (filteredReportData.thresholds.accuracyT3+1)})
                                    ]
                                  }
                                  labelData = {[t("admin.results.allSchoolsTab.chartLabel1"), t("admin.results.allSchoolsTab.chartLabel2"), t("admin.results.allSchoolsTab.chartLabel3")]}
                                  title = {"Accuracy - " + t("admin.results.allSchoolsTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.allSchoolsTab.chartXLabel", {std: std})}
                                  yLabel = {t("admin.results.allSchoolsTab.chartYLabel", {unit: "%"})}
                                  chartName={"Class-" + std}
                                  chartInfo={
                                    (filteredReportData.school[0].recCount && filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.accuracyTotal), 0)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.accuracyTotal), 0).toLocaleString("hi"),
                                        total: filteredReportData.school.reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                /> 
                              }
                              {chartType === "line" &&
                                <LineChart 
                                  chartData = {[
                                                [
                                                  filteredReportData.allSchools.accuracyMedian,
                                                  filteredReportData.region.accuracyMedian,
                                                  Math.round(filteredReportData.school.reduce((acc, item) => {
                                                    return [
                                                      acc[0] + (item.accuracyMedian)/filteredReportData.school.length,
                                                    ]
                                                  }, [0])[0])
                                                ]
                                              ]}
                                  catLabelData={
                                    [t("commonItems.chartCatLabel_line_accuracy1")]
                                  }
                                  labelData = {[t("admin.results.allSchoolsTab.chartLabel1"), t("admin.results.allSchoolsTab.chartLabel2"), t("admin.results.allSchoolsTab.chartLabel3")]}
                                  title = {"Accuracy - " + t("admin.results.allSchoolsTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.allSchoolsTab.chartXLabel", {std: std})}
                                  yLabel = {t("admin.results.allSchoolsTab.chartYLabel", {unit: "Accuracy"})}
                                  chartName={"Class-" + std}
                                  chartInfo={
                                    (filteredReportData.school[0].recCount && filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.accuracyTotal), 0)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: filteredReportData.school.reduce((acc, item) => acc + (item.recCount - item.accuracyTotal), 0).toLocaleString("hi"),
                                        total: filteredReportData.school.reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                />
                              }
                            </>
                          :
                            <h3 style={{placeSelf: "center"}}>{t("commonItems.error_metricNotFound", {metric: "Accuracy"})}</h3>
                          }
                        </>
                      }
                    </div>
                  }
                  {(reportData!=="" && lang && tabValue === 1 && school !== "") ?
                    <div className="charts">
                      {metric === "wcpm" &&
                        <>
                          {chartType === "bar" &&
                            <BarChart 
                              chartData = {
                                            reportData.school[lang]
                                              .filter(item => item.schoolName === school && item.std === std)
                                              .map(item => [item.wcpmCat1, item.wcpmCat2, item.wcpmCat3, item.wcpmCat4, item.wcpmCat5, item.wcpmTotal])
                                          }
                              catLabelData={ 
                                [
                                  filteredReportData.thresholds.wcpmT1 < 0 ? "Invalid Entries" : t("commonItems.chartCatLabel_bar_wcpm1", {t1: 0, t2: filteredReportData.thresholds.wcpmT1}),
                                  t("commonItems.chartCatLabel_bar_wcpm1", {t1: filteredReportData.thresholds.wcpmT1+1, t2: filteredReportData.thresholds.wcpmT2}),
                                  t("commonItems.chartCatLabel_bar_wcpm1", {t1: filteredReportData.thresholds.wcpmT2+1, t2: filteredReportData.thresholds.wcpmT3}),
                                  filteredReportData.thresholds.wcpmT4 < 0 ?  t("commonItems.chartCatLabel_bar_wcpm2", {t1: filteredReportData.thresholds.wcpmT3+1}) : t("commonItems.chartCatLabel_bar_wcpm1", {t1: filteredReportData.thresholds.wcpmT3+1, t2: filteredReportData.thresholds.wcpmT4}),
                                  filteredReportData.thresholds.wcpmT4 < 0 ? "-" : t("commonItems.chartCatLabel_bar_wcpm2", {t1: filteredReportData.thresholds.wcpmT4+1})
                                ]
                              }
                              labelData = {reportData.school[lang]
                                            .filter(item => item.schoolName === school && item.std === std)
                                            .map(item => item.std + item.divn)
                                          }
                              title = {"WCPM - " + t("admin.results.yourSchoolTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                              xLabel = {t("admin.results.yourSchoolTab.chartXLabel")}
                              yLabel = {t("admin.results.yourSchoolTab.chartYLabel", {unit: "%"})}
                              chartName={"Class-" + std}
                              chartInfo={ 
                                (reportData.school[lang][0].recCount && reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0)!==0) && 
                                  // "Total number of students tested = " + reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi") + 
                                  t("commonItems.info_chart", { 
                                    tested: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0).toLocaleString("hi"),
                                    total: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                  })  
                              }
                            />
                          }
                          {chartType === "line" &&
                            <LineChart 
                              chartData = {[
                                            reportData.school[lang]
                                              .filter(item => item.schoolName === school && item.std === std)
                                              .map(item => item.wcpmMedian)
                                          ]}
                              catLabelData={
                                [t("commonItems.chartCatLabel_line_wcpm1")]
                              }
                              labelData = {reportData.school[lang]
                                            .filter(item => item.schoolName === school && item.std === std)
                                            .map(item => item.std + item.divn)
                                          }
                              title = {"WCPM - " + t("admin.results.yourSchoolTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                              xLabel = {t("admin.results.yourSchoolTab.chartXLabel")}
                              yLabel = {t("admin.results.yourSchoolTab.chartYLabel", {unit: "WCPM"})}
                              chartName={"Class-" + std}
                              chartInfo={ 
                                (reportData.school[lang][0].recCount && reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0)!==0) && 
                                  // "Total number of students tested = " + reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi") + 
                                  t("commonItems.info_chart", { 
                                    tested: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0).toLocaleString("hi"),
                                    total: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                  })  
                              }
                            />
                          }
                        </>
                      }
                      {metric === "phrasing" &&
                        <>
                          {Object.keys(filteredReportData.thresholds).includes("phrasingT1") ?
                            <>
                              {chartType === "bar" &&
                                <BarChart 
                                  chartData = {
                                      reportData.school[lang]
                                        .filter(item => item.schoolName === school && item.std === std)
                                        .map(item => (
                                          showPhrasingNotQualified ?
                                            [(item.wcpmTotal - item.phrasingTotal), item.phrasingCat1, item.phrasingCat2, item.phrasingCat3, item.phrasingCat4, item.wcpmTotal]
                                          :
                                            [0, item.phrasingCat1, item.phrasingCat2, item.phrasingCat3, item.phrasingCat4, item.phrasingTotal]
                                        ))
                                  }
                                  catLabelData={
                                    showPhrasingNotQualified ?
                                      [
                                        t("commonItems.chartCatLabel_bar_phrasing1"), 
                                        t("commonItems.chartCatLabel_bar_phrasing2"), 
                                        t("commonItems.chartCatLabel_bar_phrasing3"), 
                                        t("commonItems.chartCatLabel_bar_phrasing4"), 
                                        t("commonItems.chartCatLabel_bar_phrasing5")
                                      ]
                                    :
                                      [
                                        "-", 
                                        t("commonItems.chartCatLabel_bar_phrasing2"), 
                                        t("commonItems.chartCatLabel_bar_phrasing3"), 
                                        t("commonItems.chartCatLabel_bar_phrasing4"), 
                                        t("commonItems.chartCatLabel_bar_phrasing5")
                                      ]
                                  }
                                  labelData = {reportData.school[lang]
                                                .filter(item => item.schoolName === school && item.std === std)
                                                .map(item => item.std + item.divn)
                                              }
                                  title = {"Phrasing - " + t("admin.results.yourSchoolTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.yourSchoolTab.chartXLabel")}
                                  yLabel = {t("admin.results.yourSchoolTab.chartYLabel", {unit: "%"})}
                                  // chartColors={["#FFB5B5", "#E8CFFA", "#8CCDFF", "#AAEACB"]}
                                  chartName={"Class-" + std}
                                  chartInfo={ 
                                    (reportData.school[lang][0].recCount && reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0)!==0) && 
                                      // "Total number of students tested = " + reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi") + 
                                      t("commonItems.info_chart", { 
                                        tested: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0).toLocaleString("hi"),
                                        total: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                />
                              }
                              {chartType === "line" &&
                                <LineChart 
                                  chartData = {[
                                                reportData.school[lang]
                                                  .filter(item => item.schoolName === school && item.std === std)
                                                  .map(item => item.phrasingMedian)
                                              ]}
                                  catLabelData={
                                    [t("commonItems.chartCatLabel_line_phrasing1")]
                                  }
                                  labelData = {reportData.school[lang]
                                                .filter(item => item.schoolName === school && item.std === std)
                                                .map(item => item.std + item.divn)
                                              }
                                  title = {"Phrasing - " + t("admin.results.yourSchoolTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.yourSchoolTab.chartXLabel")}
                                  yLabel = {t("admin.results.yourSchoolTab.chartYLabel", {unit: "Phrasing"})}
                                  chartName={"Class-" + std}
                                  chartInfo={ 
                                    (reportData.school[lang][0].recCount && reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0)!==0) && 
                                      // "Total number of students tested = " + reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi") + 
                                      t("commonItems.info_chart", { 
                                        tested: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.wcpmTotal), 0).toLocaleString("hi"),
                                        total: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                />
                              }
                            </>
                          :
                            <h3 style={{placeSelf: "center"}}>{t("commonItems.error_metricNotFound", {metric: "Phrasing"})}</h3>
                          }
                        </>
                      }
                      {metric === "pace" &&
                        <> 
                          {Object.keys(filteredReportData.thresholds).includes("paceT1") ?
                            <>
                              {chartType === "bar" &&
                                <BarChart 
                                  chartData = {
                                                reportData.school[lang]
                                                  .filter(item => item.schoolName === school && item.std === std)
                                                  .map(item => [item.paceCat1, item.paceCat2, item.paceCat3, item.paceCat4, item.paceTotal])
                                              }
                                  catLabelData={ 
                                    [
                                      // "Slow", "Medium", "Fast", "Very Fast"
                                      t("commonItems.chartCatLabel_bar_pace1", {t1: (parseFloat(filteredReportData.thresholds.paceT1) + 0.1).toFixed(1)}),
                                      t("commonItems.chartCatLabel_bar_pace2", {t1: (parseFloat(filteredReportData.thresholds.paceT1) + 0.1).toFixed(1), t2: (parseFloat(filteredReportData.thresholds.paceT2) + 0.1).toFixed(1)}),
                                      t("commonItems.chartCatLabel_bar_pace3", {t1: (parseFloat(filteredReportData.thresholds.paceT2) + 0.1).toFixed(1), t2: (parseFloat(filteredReportData.thresholds.paceT3) + 0.1).toFixed(1)}),
                                      t("commonItems.chartCatLabel_bar_pace4", {t1: (parseFloat(filteredReportData.thresholds.paceT3) + 0.1).toFixed(1)})
                                    ]
                                  }
                                  labelData = {reportData.school[lang]
                                                .filter(item => item.schoolName === school && item.std === std)
                                                .map(item => item.std + item.divn)
                                              }
                                  title = {"Pace - " + t("admin.results.yourSchoolTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.yourSchoolTab.chartXLabel")}
                                  yLabel = {t("admin.results.yourSchoolTab.chartYLabel", {unit: "%"})}
                                  chartName={"Class-" + std}
                                  chartInfo={ 
                                    (reportData.school[lang][0].recCount && reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.paceTotal), 0)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.paceTotal), 0).toLocaleString("hi"),
                                        total: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                />
                              }
                              {chartType === "line" &&
                                <LineChart 
                                  chartData = {[
                                                reportData.school[lang]
                                                  .filter(item => item.schoolName === school && item.std === std)
                                                  .map(item => item.paceMedian)
                                              ]}
                                  catLabelData={
                                    [t("commonItems.chartCatLabel_line_pace1")]
                                  }
                                  labelData = {reportData.school[lang]
                                                .filter(item => item.schoolName === school && item.std === std)
                                                .map(item => item.std + item.divn)
                                              }
                                  title = {"Pace - " + t("admin.results.yourSchoolTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.yourSchoolTab.chartXLabel")}
                                  yLabel = {t("admin.results.yourSchoolTab.chartYLabel", {unit: "Pace"})}
                                  chartName={"Class-" + std}
                                  chartInfo={ 
                                    (reportData.school[lang][0].recCount && reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.paceTotal), 0)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.paceTotal), 0).toLocaleString("hi"),
                                        total: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                />
                              }
                            </>
                          :
                            <h3 style={{placeSelf: "center"}}>{t("commonItems.error_metricNotFound", {metric: "Pace"})}</h3>
                          }
                        </>
                      }
                      {metric === "accuracy" &&
                        <> 
                          {Object.keys(filteredReportData.thresholds).includes("accuracyT1") ?
                            <>
                              {chartType === "bar" &&
                                <BarChart 
                                  chartData = {
                                                reportData.school[lang]
                                                  .filter(item => item.schoolName === school && item.std === std)
                                                  .map(item => [item.accuracyCat1, item.accuracyCat2, item.accuracyCat3, item.accuracyCat4, item.accuracyTotal])
                                              }
                                  catLabelData={ 
                                    [
                                      // "Poor", "Fair", "Good", "Excellent"
                                      t("commonItems.chartCatLabel_bar_accuracy1", {t1: (filteredReportData.thresholds.accuracyT1+1)}),
                                      t("commonItems.chartCatLabel_bar_accuracy2", {t1: (filteredReportData.thresholds.accuracyT1+1), t2: (filteredReportData.thresholds.accuracyT2+1)}),
                                      t("commonItems.chartCatLabel_bar_accuracy3", {t1: (filteredReportData.thresholds.accuracyT2+1), t2: (filteredReportData.thresholds.accuracyT3+1)}),
                                      t("commonItems.chartCatLabel_bar_accuracy4", {t1: (filteredReportData.thresholds.accuracyT3+1)})
                                    ]
                                  }
                                  labelData = {reportData.school[lang]
                                                .filter(item => item.schoolName === school && item.std === std)
                                                .map(item => item.std + item.divn)
                                              }
                                  title = {"Accuracy - " + t("admin.results.yourSchoolTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.yourSchoolTab.chartXLabel")}
                                  yLabel = {t("admin.results.yourSchoolTab.chartYLabel", {unit: "%"})}
                                  chartName={"Class-" + std}
                                  chartInfo={ 
                                    (reportData.school[lang][0].recCount && reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.accuracyTotal), 0)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.accuracyTotal), 0).toLocaleString("hi"),
                                        total: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                />
                              }
                              {chartType === "line" &&
                                <LineChart 
                                  chartData = {[
                                                reportData.school[lang]
                                                  .filter(item => item.schoolName === school && item.std === std)
                                                  .map(item => item.accuracyMedian)
                                              ]}
                                  catLabelData={
                                    [t("commonItems.chartCatLabel_line_accuracy1")]
                                  }
                                  labelData = {reportData.school[lang]
                                                .filter(item => item.schoolName === school && item.std === std)
                                                .map(item => item.std + item.divn)
                                              }
                                  title = {"Accuracy - " + t("admin.results.yourSchoolTab.chartTitle") + " - " + adminInfo.allLangs[lang]} 
                                  xLabel = {t("admin.results.yourSchoolTab.chartXLabel")}
                                  yLabel = {t("admin.results.yourSchoolTab.chartYLabel", {unit: "Accuracy"})}
                                  chartName={"Class-" + std}
                                  chartInfo={ 
                                    (reportData.school[lang][0].recCount && reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.accuracyTotal), 0)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + (item.recCount - item.accuracyTotal), 0).toLocaleString("hi"),
                                        total: reportData.school[lang].filter(item => item.schoolName === school && item.std === std).reduce((acc, item) => acc + item.recCount, 0).toLocaleString("hi")
                                      })  
                                  }
                                />
                              }
                            </>
                          :
                            <h3 style={{placeSelf: "center"}}>{t("commonItems.error_metricNotFound", {metric: "Accuracy"})}</h3>
                          }
                        </>
                      }
                    </div>
                    : (reportData!=="" && lang && tabValue === 1) &&
                    <div className="charts" style={{alignItems: "center"}}>
                      <h3 style={{placeSelf: "center"}}>
                        {t("admin.results.yourSchoolTab.noSchoolSelectedInfo")}
                      </h3>
                    </div>
                  }
                  {( reportData!=="" && lang && tabValue === 2) &&
                    <div className="studentsContent">
                      <div className="tabs" style={{paddingTop: "0.5rem", justifyContent: "space-between"}}>
                        <Button variant="contained" color="info" startIcon={<FileDownloadIcon />} sx={{ placeSelf: "start", minWidth: "fit-content" }}>
                          <CSVLink 
                            data={
                              filteredReportData.students.map(stud => {
                                let thresholds = filteredReportData.thresholds 
                                let stats = stud.stats[lang]
                                let multiplier = 1000 // any number to stagger the wcpm values  
                                return {
                                  ...stud, 
                                  "studentName": stud["studentName"].toUpperCase(),
                                  "classSection": stud["std"] + "-" + stud["divn"],
                                  "readerCatNum": stats.wcpm < 0 ? stats.wcpm : 
                                                stats.accuracy < 85 ? 1 * multiplier + stats.wcpm : 
                                                stats.accuracy < 90 || stats.wcpm < 85 ? 2 * multiplier + stats.wcpm  :
                                                stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? 3 * multiplier + stats.wcpm  :
                                                stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? 4 * multiplier + stats.wcpm  : "Not Computed",
                                  "readerCat": stats.wcpm === -4 ? "Processing" : 
                                              stats.wcpm === -3 ? "Not Tested/Uploaded" : 
                                              stats.wcpm === -2 ? "Audio Missing" : 
                                              stats.wcpm === -1 ? "Invalid Attempt" : 
                                              stats.accuracy < 85 ? "Beginner Reader" :
                                              stats.accuracy < 90 || stats.wcpm < 85 ? "Basic Reader" :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? "Advanced Reader" :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? "Expert Reader" : "Not Computed",
                                  "wcpm": stats.wcpm === -4 ? "Processing" : stats.wcpm === -3 ? "Not Tested/Uploaded" : stats.wcpm === -2 ? "Audio Missing" : stats.wcpm === -1 ? "Invalid Attempt" : stats.wcpm,
                                  "wcpmCat": thresholds.wcpmT1 < 0 || stats.wcpm < 0 ? "-" : 
                                                stats.wcpm <=thresholds.wcpmT1 ? "WCPM between 0 - " + thresholds.wcpmT1 :
                                                stats.wcpm <=thresholds.wcpmT2 ? "WCPM between " + (thresholds.wcpmT1+1) + " - " + thresholds.wcpmT2 : 
                                                stats.wcpm <=thresholds.wcpmT3 ? "WCPM between " + (thresholds.wcpmT2+1) + " - " + thresholds.wcpmT3 : 
                                                thresholds.wcpmT4 < 0 ? 
                                                  "WCPM >= " + (thresholds.wcpmT3+1)
                                                :
                                                  stats.wcpm <= thresholds.wcpmT4 ? "WCPM between " + (thresholds.wcpmT3+1) + " - " + thresholds.wcpmT4 : 
                                                  "WCPM >= " + (thresholds.wcpmT4+1),
                                  "phrasingCat": parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT1) ? "Poor"
                                                      : parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? "Fair"
                                                      : parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT3) ? "Good"
                                                      : parseFloat(stats.phrasing) >= 0 ? "Excellent"
                                                      : parseFloat(stats.phrasing) === -0.5 ? "Not Qualified"
                                                      : parseFloat(stats.phrasing) === -1.0 ? "Invalid Attempt" 
                                                      : parseFloat(stats.phrasing) === -2.0 ? "Audio Missing" 
                                                      : parseFloat(stats.phrasing) === -3.0 ? "Not Tested/Uploaded" 
                                                      : parseFloat(stats.phrasing) === -4.0 ? "Processing" 
                                                      : parseFloat(stats.phrasing),
                                }
                              }).sort((a, b) => { 
                                return a.readerCatNum - b.readerCatNum;
                              }).concat([
                                { "std": "", "divn": "", "studentName": "", "gender": "", "orgStudId": "", "rollNo": "", "wcpm": "", "wcpmCat": "", "phrasingCat": ""},
                                { "std": "Teacher's Sign:", "divn": "", "studentName": "", "gender": "", "orgStudId": "", "rollNo": "", "wcpm": "", "wcpmCat": "", "phrasingCat": "Principal's Sign:"},
                              ])
                            }
                            headers={headers} 
                            filename={"StudentsResultData-" + adminInfo.allLangs[lang] + "-Class-" + std + ".csv"}
                          >
                            {t("commonItems.buttonLabel_csvDownload")}
                          </CSVLink>
                        </Button>
                        <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem"}}>
                          <div style={{display: "flex", alignItems: "center", gap: "0.5rem"}}>
                            <span style={{backgroundColor: "rgb(255 181 181 / 30%)", display: "inline-flex", width: "40px", height: "15px"}}></span>
                            <p className="fontFamily-Montserrat">{t("commonItems.interventionCat1")}</p>
                          </div>
                          <div style={{display: "flex", alignItems: "center", gap: "0.5rem", marginLeft: "0.5rem"}}>
                            <span style={{backgroundColor: "rgb(170 234 203 / 30%)", display: "inline-flex", width: "40px", height: "15px"}}></span>
                            <p className="fontFamily-Montserrat">{t("commonItems.interventionCat2")}</p>
                          </div>
                        </div>
                      </div>
                      <Divider />
                      <div className="table">
                        <Table
                          columns={columns}
                          tableData={
                            filteredReportData.students.map(stud => {
                              let thresholds = filteredReportData.thresholds
                              let stats = stud.stats[lang]
                              let previousStats = filteredReportData.previousStudentResult.length && filteredReportData.previousStudentResult.filter(item => item.studId === stud.studId)[0]?.stats[lang]
                              let readerNum = stats.wcpm < 0 ? stats.wcpm : 
                                              stats.accuracy < 85 ? 1 :
                                              stats.accuracy < 90 || stats.wcpm < 85 ? 2 :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? 3 :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? 4 : -2
                              let previousNum = previousStats ? 
                                                  previousStats.wcpm < 0 ? previousStats.wcpm : 
                                                  previousStats.accuracy < 85 ? 1 :
                                                  previousStats.accuracy < 90 || previousStats.wcpm < 85 ? 2 :
                                                  previousStats.accuracy >= 90 && previousStats.wcpm >= 85 && parseFloat(previousStats.phrasing) >= parseFloat(thresholds.phrasingT2) ? 3 :
                                                  previousStats.accuracy >= 90 && previousStats.wcpm >= 85 && parseFloat(previousStats.phrasing) >= 0 ? 4 : -2
                                                : -2
                              let category = {
                                "-1": "Invalid Attempt",
                                "1": t("commonItems.textLabel_beginnerReader"),
                                "2": t("commonItems.textLabel_basicReader"),
                                "3": t("commonItems.textLabel_advancedReader"),
                                "4": t("commonItems.textLabel_expertReader")
                              }
                              // console.log(previousStats)
                              let multiplier = 1000 // any number to stagger the wcpm values  
                              return {
                                ...stud, 
                                "studentName": stud["studentName"].toUpperCase(),
                                "classSection": stud["std"] + "-" + stud["divn"],
                                "readerCatNum": stats.wcpm < 0 ? stats.wcpm : 
                                              stats.accuracy < 85 ? 1 * multiplier + stats.wcpm : 
                                              stats.accuracy < 90 || stats.wcpm < 85 ? 2 * multiplier + stats.wcpm  :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? 3 * multiplier + stats.wcpm  :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? 4 * multiplier + stats.wcpm  : "Not Computed",
                                "readerCat": stats.wcpm < 0 ? stats.wcpm : 
                                              stats.accuracy < 85 ? t("commonItems.textLabel_beginnerReader") :
                                              stats.accuracy < 90 || stats.wcpm < 85 ? t("commonItems.textLabel_basicReader") :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? t("commonItems.textLabel_advancedReader") :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? t("commonItems.textLabel_expertReader") : "Not Computed",
                                "catColor": stats.wcpm < 0 ? "unset" : 
                                              stats.accuracy < 85 ? "rgb(255 181 181 / 30%)" :                            // same as #FFB5B5 wcpm cat 1 chart color
                                              stats.accuracy < 90 || stats.wcpm < 85 ? "rgb(255 181 181 / 30%)" :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? "rgb(170 234 203 / 30%)" :              // same as #AAEACB wcpm cat 1 chart color
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? "rgb(170 234 203 / 30%)" : "unset",
                                "previousCategory": [-3, -2].includes(previousNum) || [-4, -3, -2].includes(readerNum) ? null :
                                                    previousNum === readerNum ? "Reader Category Unchanged.\nIn the last test it was " +  category[String(previousNum)] :
                                                    previousNum < readerNum ? "Reader Category Improved.\nIn the last test it was " +  category[String(previousNum)] :
                                                    "Reader Category Declined.\nIn the last test it was " +  category[String(previousNum)]
                              }
                            })
                          }
                          orderBy={["classSection", "readerCatNum"]}
                          // tableHeight="47vh" 
                          // disableSrNo
                        />
                      </div>
                    </div>
                  }
                  {( reportData!=="" && lang && tabValue === 4 ) && 
                    <div className='studentsContent'>
                      <div className="tabs">
                        <Button variant="contained" color="info" startIcon={<FileDownloadIcon />} sx={{ placeSelf: "start" }}>
                          <CSVLink 
                            headers={[
                              { label: "Class", key: "std" },
                              { label: "Section", key: "divn" },
                              { label: "Students Count", key: "studentCount" },
                              // { label: "Students Tested Count - English", key: "recCount" },
                              // { label: "Valid Report Count - English", key: "wcpmTotal" },
                              // { label: "Invalid Report Count - English", key: "enWcpmInvalid" },
                              // { label: "Students Tested Count - Hindi", key: "hiRecCount" },
                              // { label: "Valid Report Count - Hindi", key: "hiTotal" },
                              // { label: "Invalid Report Count - Hindi", key: "hiWcpmInvalid" }
                            ]}
                            data={reportData.school[lang]} 
                            filename={testId + "_AdminClassReport.csv"}
                          >
                            {t("commonItems.buttonLabel_csvDownload")}
                          </CSVLink>
                        </Button>
                      </div>
                      <Divider />
                      <div className="table">
                        <Table
                          columns={[
                            {
                              key: "classSection",
                              headerName: t("commonItems.tableHeader_classSection"),
                              width: 40
                            },
                            { 
                              key: "studentCount", 
                              headerName: t("commonItems.tableHeader_studentsCount"), 
                              width: 40 
                            },
                            {
                              key: "pdfZip",
                              headerName: t("commonItems.tableHeader_downloadReportsZip"),
                              width: 40,
                              download: true,
                              testId: testId
                            }
                          ]}
                          // tableData={reportData["school"]}
                          tableData={reportData.school[lang].map(item => ({
                            "schoolId": item["schoolId"],
                            "std": item["std"],
                            "divn": item["divn"],
                            "classSection": item["std"] + "-" + item["divn"],
                            "studentCount": item["studentCount"],
                            "pdfZip": testId && adminInfo.test && testId === adminInfo.test.testId && adminInfo.test.status === 3 ? item["schoolId"] + String(item["std"]) + item["divn"] : false,
                            })
                          )}
                          orderBy={[]}
                        />
                      </div>
                    </div>
                  }
                  {(!reportData) && <div className="charts" style={{alignItems: "center"}}><CircularProgress color="info" sx={{placeSelf: "center"}}/></div> }
                </>
                : <div className="charts" style={{alignItems: "center"}}><h3 style={{placeSelf: "center"}}>{reportError}</h3></div>
              }
          </>
        }
      </div>
    </div>
  )
}

export default Report